import { Injectable } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import { Platform } from 'ionic-angular';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { of } from 'rxjs/observable/of';
import { VasAppFacade } from '../../services/vas-app-facade.service';
import * as actions from './file.actions';

@Injectable()
export class FileDownloadEffects {

    get isDesktop(): boolean {
        return this.platform.is('core');
    }

    @Effect()
    download$ = this.actions$
        .ofType(actions.FILE_DOWNLOAD)
        .switchMap(action => {
            return this.isDesktop ?
                of(new actions.FileDownloadDesktopAction({
                    file: action.payload.file
                }) as any) :
                of(new actions.FileDownloadMobileAction({
                    file: action.payload.file
                }) as any);
        });

    @Effect()
    downloadDesktop$ = this.actions$
        .ofType(actions.FILE_DOWNLOAD_DESKTOP)
        .switchMap(action => {
            return this.appFacade.objects.file.desktop
                .fileExists(action.payload.file)
                .map(exists => {
                    return exists ?
                        new actions.FileDownloadDesktopLocalAction({
                            file: action.payload.file
                        }) :
                        new actions.FileDownloadDesktopRemoteAction({
                            file: action.payload.file
                        });
                })
                .catch(() => of(
                    new actions.FileDownloadDesktopRemoteAction({
                        file: action.payload.file
                    }))
                );
        });

    @Effect({dispatch: false})
    downloadDesktopLocal$ = this.actions$
        .ofType(actions.FILE_DOWNLOAD_DESKTOP_LOCAL)
        .switchMap(action => {
            return fromPromise(this.appFacade.objects.file.fileToBlob(
                action.payload.file
            ))
                .do(blob => this.appFacade.objects.file.desktop.saveBlobAs(
                    blob,
                    action.payload.file.name
                ))
                .catch(error => this.appFacade.toast.toastDanger(error));
        });

    @Effect({dispatch: false})
    downloadDesktopRemote$ = this.actions$
        .ofType(actions.FILE_DOWNLOAD_DESKTOP_REMOTE)
        .switchMap(action => {
            return this.appFacade.loading
                .showLoading$('Downloading file')
                .concatMap(() =>
                    this.appFacade.objects.file.api.getFileContent(
                        action.payload.file
                    )
                )
                .do(() => this.appFacade.loading.hideLoading$())
                .do(blob =>
                    this.appFacade.objects.file.desktop.saveBlobAs(
                        blob,
                        action.payload.file.name
                    )
                )
                .catch(error =>
                    this.appFacade.loading.hideLoading$()
                        .concatMap(() =>
                            this.appFacade.toast.toastDanger(error)
                        )
                );
        });

    @Effect()
    downloadMobile$ = this.actions$
        .ofType(actions.FILE_DOWNLOAD_MOBILE)
        .switchMap(action => {
            return this.appFacade.objects.file.cordova
                .fileExists(action.payload.file)
                .map(exists => {
                    return exists ?
                        new actions.FileDownloadMobileLocalAction({
                            file: action.payload.file
                        }) :
                        new actions.FileDownloadMobileRemoteAction({
                            file: action.payload.file
                        });
                })
                .catch(error =>
                    this.appFacade.toast.toastDanger(error)
                );
        });

    @Effect({dispatch: false})
    downloadMobileLocal$ = this.actions$
        .ofType(actions.FILE_DOWNLOAD_MOBILE_LOCAL)
        .switchMap((action: actions.FileDownloadMobileLocalAction) => {
            return this.appFacade.objects.file.cordova
                .openFile(
                    action.payload.file.localFileSystemPath,
                    action.payload.file.mimeType
                )
                .catch(error =>
                    this.appFacade.toast.toastDanger(error)
                );
        });

    @Effect({dispatch: false})
    downloadMobileRemote$ = this.actions$
        .ofType(actions.FILE_DOWNLOAD_MOBILE_REMOTE)
        .switchMap((action: actions.FileDownloadMobileRemoteAction) => {
            return this.appFacade.loading
                .showLoading$('Downloading file')
                .concatMap(() =>
                    this.appFacade.objects.file.cordova.streamRemoteFileToDisk(
                        action.payload.file
                    )
                )
                .do(() => this.appFacade.loading.hideLoading$())
                .concatMap(fileEntry =>
                    this.appFacade.objects.file.cordova.openFile(
                        fileEntry.toURL(),
                        action.payload.file.mimeType
                    )
                )
                .catch(error =>
                    this.appFacade.loading.hideLoading$()
                        .concatMap(() =>
                            this.appFacade.toast.toastDanger(error)
                        )
                );
        });

    constructor(
        protected actions$: Actions,
        protected platform: Platform,
        protected appFacade: VasAppFacade
    ) {
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/file/file-download.effects.ts